import { apiService } from "@/services/api";
const STORAGE_KEY = 'fitpal_intake_progress';

// ============================================
// FITPAL HEALTH INTAKE DATA
// ============================================

// ============================================
// DISQUALIFYING CONDITIONS
// ============================================
export const DISQUALIFYING_CONDITIONS = [
  'Gastroparesis (Paralysis of your intestines)',
  'Triglycerides over 600 at any point',
  'Pancreatic cancer',
  'Pancreatitis',
  'Type 1 Diabetes',
  'Hypoglycemia (low blood sugar)',
  'Insulin-dependent diabetes',
  'Thyroid cancer',
  'Family history of thyroid cancer',
  'Personal or family history of Multiple Endocrine Neoplasia (MEN-2) syndrome',
  'Anorexia or bulimia',
  'medullary-thyroid-cancer',
  'men-2-syndrome',
  'eating-disorder',
  'liver-failure',
  'ckd-stage-3b',
  'siadh',
  'symptomatic-gallstones'
];

export const DISQUALIFYING_GLP_ALLERGIES = [
    'Semaglutide (Ozempic, Wegovy)',
    'Tirzepatide (Mounjaro, Zepbound)',
    'Other GLP-1 receptor agonists'
];

export const DISQUALIFYING_DIABETES_MEDS = [

 'Insulin',
'Glimepiride (Amaryl)',
'Meglitinides such as repaglinide or nateglinide',
'Glipizide (Glucotrol and Glucotrol XL)',
'Glyburide (Micronase, Glynase, and Diabeta)',
'Sitagliptin',
'Saxagliptin',
'Linagliptin',
'Alogliptin'
];

export const CONDITIONS_REQUIRING_FOLLOWUP = [
  'type-2-diabetes'
];

// Conditions that require specific consent modals
export const CONDITIONS_REQUIRING_CONSENT = {
  'asymptomatic-gallstones': 'gallstone-consent',
  'cholecystectomy': 'cholecystectomy-consent',
  'thyroid-issues': 'thyroid-consent'
};

// ============================================
// INITIAL QUESTIONS
// ============================================
const weightLossGoalQuestion = {
  id: 'weight-loss-goal',
  type: 'single-select',
  question: 'What is your weight loss goal?',
  options: [
    { label: '10 to 20 lbs', value: '10-20' },
    { label: '21 to 50 lbs', value: '21-50' },
    { label: '51 to 100 lbs', value: '51-100' },
    { label: 'Over 100 lbs', value: 'over-100' }
  ]
};

const weightLossImprovementQuestion = {
  id: 'weight-loss-improvement',
  type: 'single-select',
  question: 'What do you hope to improve by losing weight?',
  options: [
    { label: 'Overall health and longevity', value: 'Overall health and longevity' },
    { label: 'Energy and mobility', value: 'Energy and mobility' },
    { label: 'Confidence and appearance', value: 'Confidence and appearance' },
    { label: 'Managing a health condition', value: 'Managing a health condition' },
    { label: 'All of the above', value: 'All of the above' }
  ]
};

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'multi-select-conditional',
  question: 'Do you have any of the following medical conditions?',
  subtitle: 'Select all that apply',
  options: [
    // Disqualifying conditions
    { label: 'Gastroparesis (Paralysis of your intestines)', value: 'Gastroparesis (Paralysis of your intestines)', disqualifies: true },
    { label: 'Triglycerides over 600 at any point', value: 'Triglycerides over 600 at any point', disqualifies: true },
    { label: 'Pancreatic cancer', value: 'Pancreatic cancer', disqualifies: true },
    { label: 'Pancreatitis', value: 'Pancreatitis', disqualifies: true },
    { label: 'Type 1 Diabetes', value: 'Type 1 Diabetes', disqualifies: true },
    { label: 'Hypoglycemia (low blood sugar)', value: 'Hypoglycemia (low blood sugar)', disqualifies: true },
    { label: 'Insulin-dependent diabetes', value: 'Insulin-dependent diabetes', disqualifies: true },
    { label: 'Thyroid cancer', value: 'Thyroid cancer', disqualifies: true },
    { label: 'Family history of thyroid cancer', value: 'Family history of thyroid cancer', disqualifies: true },
    { label: 'Personal or family history of Multiple Endocrine Neoplasia (MEN-2) syndrome', value: 'Personal or family history of Multiple Endocrine Neoplasia (MEN-2) syndrome', disqualifies: true },
    { label: 'Anorexia or bulimia', value: 'Anorexia or bulimia', disqualifies: true },
    { label: 'Current symptomatic gallstones', value: 'Current symptomatic gallstones', disqualifies: true },
    // { label: 'Liver failure/liver cirrhosis', value: 'liver-failure', disqualifies: true },
    // { label: 'Chronic Kidney Disease Stage 3b or greater', value: 'ckd-stage-3b', disqualifies: true },
    // { label: 'Syndrome of Inappropriate Antidiuretic hormone', value: 'siadh', disqualifies: true },
    // { label: 'Current symptomatic gallstones', value: 'symptomatic-gallstones', disqualifies: true },
    // Qualifying conditions (require consent)
    { label: 'Current gallstones without symptoms', value: 'asymptomatic-gallstones', requiresConsent: 'gallstone-consent' },
    { label: 'Past removal of your gallbladder', value: 'cholecystectomy', requiresConsent: 'cholecystectomy-consent' },
    { label: 'Hypothyroidism, Hyperthyroidism, or Thyroid Issues', value: 'thyroid-issues', requiresConsent: 'thyroid-consent' },
    // Other conditions
    { label: 'Type 2 Diabetes', value: 'type-2-diabetes' },
    { label: 'None of the above', value: 'none' }
  ]
};

const gallstoneConsentQuestion = {
  id: 'gallstone-consent',
  type: 'consent-dropdown',
  question: 'Gallstone Information',
  consentText: `Use of GLP-1 medications, including semaglutide, tirzepatide, and similar products, has been associated with an increased risk of gallbladder or biliary issues, including the development or worsening of gallstones. If you have existing but asymptomatic gallstones, treatment may increase the chance that these stones become symptomatic, potentially leading to pain, inflammation, or the need for medical or surgical treatment. Please notify your provider if you have a history of gallstones or experience new upper abdominal pain, nausea, or vomiting during therapy. Knowing this risk, do you wish to continue?`,
  options: [
    { label: 'I acknowledge that I have read and understood the above information and wish to continue.', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const cholecystectomyConsentQuestion = {
  id: 'cholecystectomy-consent',
  type: 'consent-dropdown',
  question: 'Cholecystectomy (Gallbladder Removal) Information',
  consentText: `You reported a history of gallbladder disease or gallbladder removal. This medication may still be appropriate, but it can affect how your body digests fats and bile. Without a gallbladder, bile flow and fat digestion are altered, which may increase the risk of side effects like stomach pain or diarrhea.

This medication may also impact absorption of certain nutrients (especially vitamins A, D, E, and K). To reduce risk, eat smaller, frequent meals, avoid processed foods, and consider a daily multivitamin unless otherwise directed by your provider.

If you have gallstones, please note that weight loss and this medication can increase the risk of gallstone formation or blockage, which can cause severe pain, infection, or pancreatitis. Seek immediate medical care for abdominal pain, fever, or vomiting.`,
  options: [
    { label: 'I acknowledge that I have read and understood the above information and wish to continue.', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const thyroidConsentQuestion = {
  id: 'thyroid-consent',
  type: 'consent-dropdown',
  question: 'Thyroid Condition Information',
  consentText: `These medications can affect how your body absorbs or responds to thyroid therapy. If you have thyroid disease, your provider may need to adjust your dosage or monitor your thyroid levels more closely after starting treatment.`,
  options: [
    { label: 'I acknowledge that I have read and understood the above information', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const medicalConditionDetailsQuestion = {
  id: 'medical-condition-details',
  type: 'textarea',
  question: 'Please provide more details about your medical condition(s)',
  subtitle: 'This information helps our providers create a safe treatment plan for you',
  placeholder: 'Please describe your condition, when it was diagnosed, current treatments, etc.'
};

// Fires when computed BMI ∈ [25, 29] AND no comorbidity is selected on
// `medical-conditions`. Off-label-use acknowledgement required by Beluga
// (Drive doc 1VGu_E9cgMjX6Fu9MwJi0PgDMFuG0k1lWAxcA4rj6MzI, Q6).
const bmiConsentQuestion = {
  id: 'bmi-consent',
  type: 'consent-dropdown',
  question: 'Consent (BMI)',
  consentText: `Weight loss medications are traditionally prescribed for individuals with a BMI of 30 or higher, or for those who are overweight (BMI 25–29) with a related health condition. Using these medications for someone in the 25–29 BMI range without another health condition is considered "off-label" use. This means the medication is being prescribed in a way not specifically approved by the U.S. Food and Drug Administration (FDA). Please follow your prescribed regimen closely and report any side effects or concerns.`,
  options: [
    { label: 'I acknowledge that I have read and understood the above information', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const medicationsListQuestion = {
  id: 'medications-list',
  type: 'textarea',
  question: 'Please list all medications you are currently taking',
  subtitle: 'Include prescription medications, over-the-counter drugs, and supplements',
  placeholder: 'e.g., Metformin 500mg twice daily, Lisinopril 10mg daily, Vitamin D 2000 IU daily...',
  required:true
};

const currentMedicalConditions = {
  id: 'current-medical-condition',
  type: 'textarea',
  question: 'Please identify all your current medical conditions',
  subtitle: 'Include chronic conditions, past surgeries, and any other relevant health issues',
  placeholder: 'e.g., Hypertension, Asthma, Previous knee surgery, etc...',
  required:true
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea',
  question: 'Do you have any known allergies?',
  subtitle: 'Include drug allergies, food allergies, and environmental allergies',
  placeholder: 'e.g., Penicillin (causes rash), Shellfish (anaphylaxis), Seasonal allergies...',
  required:true
};

const sexAtBirthQuestion = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: 'What is your sex assigned at birth?',
  options: [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' }
  ]
};

const basicContactQuestion = {
  id: 'basic-contact',
  type: 'basic-contact',
  question: 'How can we reach you?',
  subtitle: "We'll use this to send updates about your treatment"
};

const pregnancyQuestion = {
  id: 'pregnancy-status',
  type: 'single-select',
  question: 'Are you currently pregnant, breastfeeding, or planning to become pregnant in the next 6 months?',
  options: [
    { label: 'Yes', value: 'yes', requiresConsent: true },
    { label: 'No', value: 'no' }
  ]
};

const pregnancyConsentQuestion = {
  id: 'pregnancy-consent',
  type: 'consent-dropdown',
  question: 'Pregnancy Acknowledgement',
  consentText: `GLP-1 medications are not recommended during pregnancy or breastfeeding due to potential risks to the fetus or infant. 

If you are currently pregnant, breastfeeding, or planning to become pregnant soon, it is important to discuss alternative treatment options with your healthcare provider.

Women of childbearing potential should use effective contraception while taking GLP-1 medications.

By proceeding, you acknowledge that:
• You understand the risks associated with GLP-1 medications during pregnancy
• You will inform your healthcare provider immediately if you become pregnant
• You will use appropriate contraception if sexually active`,
  options: [
    { label: 'I acknowledge this information and wish to proceed', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const highestWeightQuestion = {
  id: 'highest-weight',
  type: 'number',
  question: 'What was the highest weight that you have reached?',
  subtitle: 'This helps us understand your weight history',
  placeholder: 'Enter weight in lbs',
  inputType: 'number',
  min: 50,
  max: 800,
  suffix: 'lbs'
};

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'Tell us about your measurements',
  subtitle: 'This information is used to calculate your BMI'
};

const diagnosisTimeQuestion = {
  id: 'diagnosis-time',
  type: 'single-select',
  question: 'When were you first diagnosed with being overweight or obese?',
  options: [
    { label: 'Within the last year', value: 'Within the last year' },
    { label: '1–5 years ago', value: '1–5 years ago' },
    { label: 'More than 5 years ago', value: 'More than 5 years ago' },
    { label: 'Unsure', value: 'Unsure' },
  ]
};

const weightManagementApproachQuestion = {
  id: 'weight-management-approach',
  type: 'single-select',
  question: 'How would you describe your approach to weight management?',
  options: [
    { label: 'Actively managing my weight through diet and exercise', value: 'Actively managing' },
    { label: 'Trying various methods with limited success', value: 'Some efforts' },
    { label: 'Haven\'t been able to focus on weight management', value: 'No active efforts' },
  ]
};

const activityLevelQuestion = {
  id: 'activity-level',
  type: 'single-select',
  question: 'What is your current physical activity level?',
  options: [
    { label: 'Sedentary (little or no exercise)', value: 'Sedentary (I don’t exercise)' },
    { label: 'Light (1-3 days per week)', value: 'Light' },
    { label: 'Moderate (3-5 days per week)', value: 'Moderate' },
    { label: 'Active (6-7 days per week)', value: 'Vigorous' },
  ]
};

const dietaryPlanQuestion = {
  id: 'dietary-plan',
  type: 'single-select',
  question: 'Are you currently following a specific dietary plan?',
  options: [
    { label: 'No specific plan', value: 'None' },
    { label: 'Minor modifications', value: 'Minor modifications' },
    { label: 'Significant dietary changes', value: 'Significant dietary changes' }
  ]
};

const gastricBypassQuestion = {
  id: 'gastric-bypass',
  type: 'single-select',
  question: 'Have you had gastric bypass or bariatric surgery in the past 6 months?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' }
  ]
};

const glpAllergiesQuestion = {
  id: 'glp-allergies',
  type: 'multi-select',
  question: 'Do you have any allergies to GLP-1 medications?',
  subtitle: 'Select all that apply',
  options: [
    { label: 'Semaglutide (Ozempic, Wegovy)', value: 'Semaglutide (Ozempic, Wegovy)', disqualifies: true },
    { label: 'Tirzepatide (Mounjaro, Zepbound)', value: 'Tirzepatide (Mounjaro, Zepbound)', disqualifies: true },
    { label: 'Other GLP-1 receptor agonists', value: 'Other GLP-1 receptor agonists', disqualifies: true },
    { label: 'None of the above', value: 'None of the above' }
  ]
};

const diabetesMedicationsQuestion = {
  id: 'diabetes-medications',
  type: 'multi-select',
  question: 'Are you currently taking any of the following diabetes medications?',
  subtitle: 'These medications may interact with GLP-1 treatment',
  options: [
    { label: 'Insulin', value: 'Insulin', disqualifies: true },
    { label: 'Glimepiride (Amaryl)', value: 'Glimepiride (Amaryl)', disqualifies: true },
    { label: 'Meglitinides such as repaglinide or nateglinide', value: 'Meglitinides such as repaglinide or nateglinide', disqualifies: true },
    { label: 'Glipizide (Glucotrol and Glucotrol XL)', value: 'Glipizide (Glucotrol and Glucotrol XL)', disqualifies: true },
    { label: 'Glyburide (Micronase, Glynase, and Diabeta)', value: 'Glyburide (Micronase, Glynase, and Diabeta)', disqualifies: true },
    { label: 'Sitagliptin', value: 'Sitagliptin', disqualifies: true },
    { label: 'Saxagliptin', value: 'Saxagliptin', disqualifies: true },
    { label: 'Linagliptin', value: 'Linagliptin', disqualifies: true },
    { label: 'Alogliptin', value: 'Alogliptin', disqualifies: true },
    { label: 'None of the above', value: 'None of the above' }
  ]
};

const currentGlpMedicationsQuestion = {
  id: 'current-glp-medications',
  type: 'multi-select-conditional',
  question: 'Are you currently, or have you in the past two months, taken any of the following medications?',
  subtitle: 'Select all that apply',
  triggersPatientOnTreatmentFlow: true,
  options: [
    { label: 'Semaglutide (Ozempic, Wegovy, Rybelsus)', value: 'semaglutide', triggersFlow: 'on-treatment' },
    { label: 'Tirzepatide (Zepbound, Mounjaro)', value: 'tirzepatide', triggersFlow: 'on-treatment' },
    { label: 'None of these', value: 'none', triggersFlow: 'new-patient' }
  ]
};


// ============================================
// PATIENT ON TREATMENT FLOW QUESTIONS
// ============================================
const lastMedicationDateQuestion = {
  id: 'last-medication-date',
  type: 'single-select-conditional',
  question: 'When did you last take your prescribed weight loss medication?',
  options: [
    { label: 'Within the past week', value: 'Within the past week' },
    { label: 'Within the past 2-3 weeks', value: 'Within the past 2-3 weeks', triggersQuestion: 'dose-decrease' },
    // { label: 'Within the past month', value: 'Within the past month', triggersQuestion: 'dose-decrease' },
    // { label: 'It has been over a month', value: 'It has been over a month', triggersQuestion: 'restart-medication' },
    { label: 'It has been over a month', value: 'It has been over a month', triggersQuestion: 'restart-medication', starterDose: true }
  ]
};

const doseDecreaseQuestion = {
  id: 'dose-decrease',
  type: 'consent-dropdown',
  question: 'Dose Reduction Notice',
  consentText: `I'm so sorry, but these medications gradually build up in your system. Since you've been off this medication for an extended period, it's safest to restart at a lower dose, typically about 50% of your previous dose, and titrate back up. Restarting at a high dose after a break can cause significant side effects, sometimes requiring emergency care or hospitalization. Would you like to continue with a lower starting dose?`,
  options: [
    { label: 'I understand and agree to the dose reduction', value: 'consent' },
    { label: 'I do not agree and do not wish to continue', value: 'decline', disqualifies: true }
  ]
};

const restartMedicationQuestion = {
  id: 'restart-medication',
  type: 'consent-dropdown',
  question: 'Restart Medication Notice',
  consentText: `I'm so sorry, but these medications gradually build up in your system. Since you've been off this medication for an extended period, it's safest to restart at the starter dose and titrate back up. Restarting at a high dose after a break can cause significant side effects, sometimes requiring emergency care or hospitalization. Would you like to continue with a lower starting dose?`,
  patientPreference: 'starter-dose',
  options: [
    { label: 'I understand and agree to the dose reduction', value: 'consent', starterDose: true },
    { label: 'I do not agree and do not wish to continue', value: 'decline', disqualifies: true }
  ]
};

const lastMedicationDateInputQuestion = {
  id: 'last-medication-date-input',
  type: 'text',
  question: 'Please provide the approximate date (month and day) of your last use of this medication',
  placeholder: 'e.g., January 15',
  inputType: 'text'
};

const currentDoseQuestion = {
  id: 'current-dose',
  type: 'single-select',
  question: 'Which medication and dose most closely matches your most recent dose?',
  subtitle: 'Tirzepatide = Mounjaro, Zepbound | Semaglutide = Ozempic, Wegovy',
  options: [
    { label: 'Semaglutide 0.25mg', value: 'Semaglutide 0.25mg' },
    { label: 'Semaglutide 0.5mg', value: 'Semaglutide 0.5mg' },
    { label: 'Semaglutide 1mg', value: 'Semaglutide 1mg' },
    { label: 'Semaglutide 1.7mg', value: 'Semaglutide 1.7mg' },
    { label: 'Semaglutide 2.4mg', value: 'Semaglutide 2.4mg' },
    { label: 'Tirzepatide 2.5mg', value: 'Tirzepatide 2.5mg' },
    { label: 'Tirzepatide 5mg', value: 'Tirzepatide 5mg' },
    { label: 'Tirzepatide 7.5mg', value: 'Tirzepatide 7.5mg' },
    { label: 'Tirzepatide 10mg', value: 'Tirzepatide 10mg' },
    { label: 'Tirzepatide 12.5mg', value: 'Tirzepatide 12.5mg' },
    { label: 'Tirzepatide 15mg', value: 'Tirzepatide 15mg' }
  ]
};

const brandOrCompoundedQuestion = {
  id: 'brand-or-compounded',
  type: 'single-select-conditional',
  question: 'Are you currently using a brand name medication (like Wegovy or Zepbound) or a compounded version?',
  options: [
    { label: "I'm taking a brand name medication", value: "I'm taking a brand name medication", skipTo: 'side-effects' },
    { label: "I'm taking a compounded product", value: "I'm taking a compounded product", goTo: 'medication-details' }
  ]
};

const medicationDetailsQuestion = {
  id: 'medication-details',
  type: 'textarea-with-upload',
  question: 'Please tell us the name and dose of the weight loss medication you are taking',
  subtitle: `Safety is our top priority and to ensure that your provider can verify your current dose accurately, please share the information below as clearly as possible. Providing the exact details helps us confirm that your treatment plan is correct and safe. Here's how to share the information clearly:

1. Medication name – write the exact name from your prescription label including any additives such as glycine, vitamin B, etc.
2. Concentration – look on your prescription label or vial for something that looks like "20 mg/mL" or "2.5 mg/mL." There may be several numbers which should all be provided. This shows how strong your medication is. Please provide all the numbers listed.
3. Amount you took – write how much you injected or used, in units or milliliters (mL).
(Example: "Medication: Semaglutide. Concentration: 2.5 mg/mL. Dose taken: 0.5 mL.")
4. If you're unsure about your prescription details, please upload a clear photo of your medication label. Make sure the entire label is visible, and let us know how many units or milliliters (mL) you are currently using.`,
  placeholder: 'e.g., Medication: Semaglutide. Concentration: 2.5 mg/mL. Dose taken: 0.5 mL.'
};

const sideEffectsQuestion = {
  id: 'side-effects',
  type: 'multi-select-with-optional-addons',
  question: 'Have you experienced any of the following side effects from your current medication?',
  subtitle: 'Select all that apply',
  options: [
    { 
      label: 'Yes, I have experienced fatigue and WOULD like to explore the option of including an additive which may help manage my fatigue symptoms', 
      value: 'fatigue',
      hasOptionalAdditive: true,
      additiveLabel: 'Optional: Select an additive to help with fatigue',
      // NAD+ removed (2026-06-28): no compounded WL SKU and it requires its own
      // anti-aging intake — not eligible as a weight-loss additive.
      additives: [
        { label: 'B12 (Vitamin B12)', value: 'b12' },
        { label: 'B3 (Niacin)', value: 'b3' },
        { label: 'No additive', value: 'none' }
      ]
    },
    { 
      label: 'Yes, I have experienced nausea and WOULD like to explore the option of including an additive which may help reduce your nausea', 
      value: 'nausea',
      hasOptionalAdditive: true,
      additiveLabel: 'Optional: Select an option to help with nausea',
      additives: [
        { label: 'B6 (Pyridoxine) - Additive', value: 'b6' },
        { label: 'Zofran® (Ondansetron 4mg) - Doctor Prescribed', value: 'zofran-prescribed' },
        { label: 'No additive', value: 'none' }
      ]
    },
    { 
      label: 'Yes, I have experienced muscle loss and WOULD like to explore the option of including an additive which may help to support muscle health', 
      value: 'muscle-wasting',
      hasOptionalAdditive: true,
      additiveLabel: 'Optional: Select an additive to support muscle health',
      additives: [
        { label: 'Glycine', value: 'glycine' },
        { label: 'L-Carnitine', value: 'l-carnitine' },
        { label: 'No additive', value: 'none' }
      ]
    },
    { label: 'No', value: 'none' }
  ]
};

// Legacy additive questions - kept for backwards compatibility but no longer used in main flow
const fatigueAdditiveQuestion = {
  id: 'fatigue-additive',
  type: 'single-select',
  question: 'Select an additive to help manage fatigue',
  subtitle: 'These additives may help with fatigue symptoms',
  options: [
    { label: 'B12 (Vitamin B12)', value: 'b12', description: 'Supports energy production and reduces fatigue' },
    { label: 'B3 (Niacin)', value: 'b3', description: 'Helps convert food into energy' },
    { label: 'NAD+ (Nicotinamide Adenine Dinucleotide)', value: 'nad-plus', description: 'Supports cellular energy and metabolism' },
    { label: 'No additive needed', value: 'none' }
  ]
};

const nauseaAdditiveQuestion = {
  id: 'nausea-additive',
  type: 'single-select',
  question: 'Select an additive to help reduce nausea',
  subtitle: 'These additives may help with nausea symptoms',
  options: [
    { label: 'B6 (Pyridoxine)', value: 'b6', description: 'Helps reduce nausea and supports digestive health' },
    { label: 'Zofran (Ondansetron 4mg)', value: 'zofran', description: 'Anti-nausea medication' },
    { label: 'No additive needed', value: 'none' }
  ]
};

const muscleAdditiveQuestion = {
  id: 'muscle-additive',
  type: 'single-select',
  question: 'Select an additive to help support muscle health',
  subtitle: 'These additives may help with muscle wasting symptoms',
  options: [
    { label: 'Glycine', value: 'glycine', description: 'Supports muscle growth and repair' },
    { label: 'L-Carnitine', value: 'l-carnitine', description: 'Helps transport fatty acids for energy and supports muscle function' },
    { label: 'No additive needed', value: 'none' }
  ]
};

const medicationProgressQuestion = {
  id: 'medication-progress',
  type: 'single-select-conditional',
  question: 'How are you responding to your medication?',
  options: [
    { label: "I'm not feeling any effects and have no side effects", value: "I'm not feeling any effects and have no side effects", triggersQuestion: 'rapid-titration' },
    { label: "I'm not feeling any effects but I'm having side effects", value: "I'm not feeling any effects but I'm having side effects", triggersQuestion: 'twice-weekly' },
    { label: "I'm feeling the effects but have side effects", value: "I'm feeling the effects but have side effects", triggersQuestion: 'twice-weekly' },
    { label: "I'm feeling the effects and have no side effects", value: "I'm feeling the effects and have no side effects", triggersQuestion: 'regimen-selection-alternative' }
  ]
};

const rapidTitrationQuestion = {
  id: 'rapid-titration',
  type: 'decision-with-regimen',
  question: "Shared decision-making is important to ensure this fits your lifestyle. Would you like to continue your current plan or transition to our Rapid Titration Protocol to increase your dose more quickly?",
  options: [
    { 
      label: "I'd like to continue my current plan and discuss adjusting my dose", 
      value: 'current-protocol',
      showRegimen: 'alternative',
      regimenTitle: 'Alternative Regimen',
      regimenSubtitle: 'Our providers have not yet reviewed your intake form but you may be a candidate for a compounded product. Based on our experience as providers, we have developed an alternative dosing protocol which may provide a more comfortable experience for you.'
    },
    { 
      label: "I'd like to transition to the Rapid Titration Protocol", 
      value: 'rapid-titration',
      showRegimen: 'rapid',
      regimenTitle: 'Rapid Regimen',
      regimenSubtitle: 'Our providers have not yet reviewed your intake form but you may be a candidate for a compounded product. Based on our experience as providers, we have developed a rapid dosing protocol which may provide a more comfortable experience for you.',
      regimenNote: '*Note: Intended for patients previously or currently on treatment who have NEVER experienced side effects'
    }
  ],
  semaglutideRegimens: {
    alternative: [
      { label: 'Semaglutide 0.2mg/0.4mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 0.2mg/0.4mg alternative - Both doses for 2 weeks each' },
      { label: 'Semaglutide 0.6mg/0.9mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 0.6mg/0.9mg alternative - Both doses for 2 weeks each' },
      { label: 'Semaglutide 1.12mg/1.5mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 1.12mg/1.5mg alternative - Both doses for 2 weeks each' },
      { label: 'Semaglutide 1.9mg alternative - 4 week duration', value: 'Semaglutide 1.9mg alternative - 4 week duration' },
      { label: 'Semaglutide 2.16mg alternative - 4 week duration', value: 'Semaglutide 2.16mg alternative - 4 week duration' },
      { label: 'Semaglutide 2.3mg alternative - 4 week duration', value: 'Semaglutide 2.3mg alternative - 4 week duration' }
    ],
    rapid: [
      { label: 'Semaglutide 0.2mg/0.4mg rapid - Both doses for 2 weeks each', value: 'Semaglutide 0.2mg/0.4mg rapid - Both doses for 2 weeks each' },
      { label: 'Semaglutide 0.6mg/0.9mg rapid - Both doses for 2 weeks each', value: 'Semaglutide 0.6mg/0.9mg rapid - Both doses for 2 weeks each' },
      { label: 'Semaglutide 1.12mg/1.5mg rapid - Both doses for 2 weeks each', value: 'Semaglutide 1.12mg/1.5mg rapid - Both doses for 2 weeks each' },
      { label: 'Semaglutide 1.9mg/2.16mg rapid - Both doses for 2 weeks each', value: 'Semaglutide 1.9mg/2.16mg rapid - Both doses for 2 weeks each' },
      { label: 'Semaglutide 2.3mg rapid - 4 week duration', value: 'Semaglutide 2.3mg rapid - 4 week duration' }
    ]
  },
  tirzepatideRegimens: {
    alternative: [
      { label: 'Tirzepatide 1.5mg/3mg alternative - Both doses for 2 weeks each', value: 'Tirzepatide 1.5mg/3mg alternative - Both doses for 2 weeks each' },
      { label: 'Tirzepatide 6mg alternative - 4 week duration', value: 'Tirzepatide 6mg alternative - 4 week duration' },
      { label: 'Tirzepatide 9mg alternative - 4 week duration', value: 'Tirzepatide 9mg alternative - 4 week duration' },
      { label: 'Tirzepatide 11mg alternative - 4 week duration', value: 'Tirzepatide 11mg alternative - 4 week duration' },
      { label: 'Tirzepatide 13.5mg alternative - 4 week duration', value: 'Tirzepatide 13.5mg alternative - 4 week duration' }
    ],
    rapid: [
      { label: 'Tirzepatide 1.5mg/3mg rapid - Both doses for 2 weeks each', value: 'Tirzepatide 1.5mg/3mg rapid - Both doses for 2 weeks each' },
      { label: 'Tirzepatide 6mg/9mg rapid - Both doses for 2 weeks each', value: 'Tirzepatide 6mg/9mg rapid - Both doses for 2 weeks each' },
      { label: 'Tirzepatide 11mg/13.5mg rapid - Both doses for 2 weeks each', value: 'Tirzepatide 11mg/13.5mg rapid - Both doses for 2 weeks each' },
      { label: 'Tirzepatide 13.5mg rapid - 4 week duration', value: 'Tirzepatide 13.5mg rapid - 4 week duration' }
    ]
  }
};

const twiceWeeklyQuestion = {
  id: 'twice-weekly',
  type: 'decision-with-regimen',
  question: "Shared decision-making is important to ensure this fits your lifestyle. Would you like to reduce side effects by splitting your dose into smaller twice-weekly injections?",
  options: [
    { 
      label: "Yes, I'd like to take a smaller dose TWICE a week", 
      value: 'twice-weekly',
      showRegimen: 'twice-weekly',
      regimenTitle: 'Twice a Week Regimen',
      regimenSubtitle: 'Our providers have not yet reviewed your intake form but you may be a candidate for a compounded product. Based on our experience as providers, we have developed a twice weekly dosing protocol which may provide a more comfortable experience for you.',
      regimenNote: '*Note: Intended for individuals who report side effects'
    },
    { 
      label: "No, I'd like to continue my current ONCE a week dose", 
      value: 'once-weekly',
      showRegimen: 'alternative',
      regimenTitle: 'Alternative Regimen',
      regimenSubtitle: 'Our providers have not yet reviewed your intake form but you may be a candidate for a compounded product. Based on our experience as providers, we have developed an alternative dosing protocol which may provide a more comfortable experience for you.'
    }
  ],
  semaglutideRegimens: {
    'twice-weekly': [
      { label: 'Semaglutide 0.2mg/0.4mg biweekly - Both doses for 2 weeks each', value: 'Semaglutide 0.2mg/0.4mg biweekly - Both doses for 2 weeks each' },
      { label: 'Semaglutide 0.6mg/0.9mg biweekly - Both doses for 2 weeks each', value: 'Semaglutide 0.6mg/0.9mg biweekly - Both doses for 2 weeks each' },
      { label: 'Semaglutide 1.12mg/1.5mg biweekly - Both doses for 2 weeks each', value: 'Semaglutide 1.12mg/1.5mg biweekly - Both doses for 2 weeks each' },
      { label: 'Semaglutide 1.9mg biweekly - 4 week duration', value: 'Semaglutide 1.9mg biweekly - 4 week duration' },
      { label: 'Semaglutide 2.16mg biweekly - 4 week duration', value: 'Semaglutide 2.16mg biweekly - 4 week duration' },
      { label: 'Semaglutide 2.3mg biweekly - 4 week duration', value: 'Semaglutide 2.3mg biweekly - 4 week duration' }
    ],
    alternative: [
      { label: 'Semaglutide 0.2mg/0.4mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 0.2mg/0.4mg alternative - Both doses for 2 weeks each' },
      { label: 'Semaglutide 0.6mg/0.9mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 0.6mg/0.9mg alternative - Both doses for 2 weeks each' },
      { label: 'Semaglutide 1.12mg/1.5mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 1.12mg/1.5mg alternative - Both doses for 2 weeks each' },
      { label: 'Semaglutide 1.9mg alternative - 4 week duration', value: 'Semaglutide 1.9mg alternative - 4 week duration' },
      { label: 'Semaglutide 2.16mg alternative - 4 week duration', value: 'Semaglutide 2.16mg alternative - 4 week duration' },
      { label: 'Semaglutide 2.3mg alternative - 4 week duration', value: 'Semaglutide 2.3mg alternative - 4 week duration' }
    ]
  },
  tirzepatideRegimens: {
    'twice-weekly': [
      { label: 'Tirzepatide 1.5mg/3mg biweekly - Both doses for 2 weeks each', value: 'Tirzepatide 1.5mg/3mg biweekly - Both doses for 2 weeks each' },
      { label: 'Tirzepatide 6mg biweekly - 4 week duration', value: 'Tirzepatide 6mg biweekly - 4 week duration' },
      { label: 'Tirzepatide 9mg biweekly - 4 week duration', value: 'Tirzepatide 9mg biweekly - 4 week duration' },
      { label: 'Tirzepatide 11mg biweekly - 4 week duration', value: 'Tirzepatide 11mg biweekly - 4 week duration' },
      { label: 'Tirzepatide 13.5mg biweekly - 4 week duration', value: 'Tirzepatide 13.5mg biweekly - 4 week duration' }
    ],
    alternative: [
      { label: 'Tirzepatide 1.5mg/3mg alternative - Both doses for 2 weeks each', value: 'Tirzepatide 1.5mg/3mg alternative - Both doses for 2 weeks each' },
      { label: 'Tirzepatide 6mg alternative - 4 week duration', value: 'Tirzepatide 6mg alternative - 4 week duration' },
      { label: 'Tirzepatide 9mg alternative - 4 week duration', value: 'Tirzepatide 9mg alternative - 4 week duration' },
      { label: 'Tirzepatide 11mg alternative - 4 week duration', value: 'Tirzepatide 11mg alternative - 4 week duration' },
      { label: 'Tirzepatide 13.5mg alternative - 4 week duration', value: 'Tirzepatide 13.5mg alternative - 4 week duration' }
    ]
  }
};

// Question for "effects-no-sides" - shows alternative regimen directly
const alternativeRegimenInfoQuestion = {
  id: 'alternative-regimen-info',
  type: 'regimen-info-display',
  question: "Based on your responses, your provider may consider the following regimen options:",
  regimenTitle: 'Alternative Regimen',
  regimenSubtitle: 'Our providers have not yet reviewed your intake form but you may be a candidate for a compounded product. Based on our experience as providers, we have developed an alternative dosing protocol which may provide a more comfortable experience for you.',
  semaglutideOptions: [
    { label: 'Semaglutide 0.2mg/0.4mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 0.2mg/0.4mg alternative - Both doses for 2 weeks each' },
    { label: 'Semaglutide 0.6mg/0.9mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 0.6mg/0.9mg alternative - Both doses for 2 weeks each' },
    { label: 'Semaglutide 1.12mg/1.5mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 1.12mg/1.5mg alternative - Both doses for 2 weeks each' },
    { label: 'Semaglutide 1.9mg alternative - 4 week duration', value: 'Semaglutide 1.9mg alternative - 4 week duration' },
    { label: 'Semaglutide 2.16mg alternative - 4 week duration', value: 'Semaglutide 2.16mg alternative - 4 week duration' },
    { label: 'Semaglutide 2.3mg alternative - 4 week duration', value: 'Semaglutide 2.3mg alternative - 4 week duration' }
  ],
  tirzepatideOptions: [
    { label: 'Tirzepatide 1.5mg/3mg alternative - Both doses for 2 weeks each', value: 'Tirzepatide 1.5mg/3mg alternative - Both doses for 2 weeks each' },
    { label: 'Tirzepatide 6mg alternative - 4 week duration', value: 'Tirzepatide 6mg alternative - 4 week duration' },
    { label: 'Tirzepatide 9mg alternative - 4 week duration', value: 'Tirzepatide 9mg alternative - 4 week duration' },
    { label: 'Tirzepatide 11mg alternative - 4 week duration', value: 'Tirzepatide 11mg alternative - 4 week duration' },
    { label: 'Tirzepatide 13.5mg alternative - 4 week duration', value: 'Tirzepatide 13.5mg alternative - 4 week duration' }
  ]
};

// Legacy regimen questions - kept for backwards compatibility
const semaglutideAlternativeRegimenQuestion = {
  id: 'semaglutide-alternative-regimen',
  type: 'single-select',
  question: 'Select your Semaglutide Alternative Regimen dose',
  subtitle: 'Alternative Regimen',
  options: [
    { label: 'Semaglutide 0.2mg/0.4mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 0.2mg/0.4mg alternative - Both doses for 2 weeks each' },
    { label: 'Semaglutide 0.6mg/0.9mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 0.6mg/0.9mg alternative - Both doses for 2 weeks each' },
    { label: 'Semaglutide 1.12mg/1.5mg alternative - Both doses for 2 weeks each', value: 'Semaglutide 1.12mg/1.5mg alternative - Both doses for 2 weeks each' },
    { label: 'Semaglutide 1.9mg alternative - 4 week duration', value: 'Semaglutide 1.9mg alternative - 4 week duration' },
    { label: 'Semaglutide 2.16mg alternative - 4 week duration', value: 'Semaglutide 2.16mg alternative - 4 week duration' },
    { label: 'Semaglutide 2.3mg alternative - 4 week duration', value: 'Semaglutide 2.3mg alternative - 4 week duration' }
  ]
};

const semaglutideRapidRegimenQuestion = {
  id: 'semaglutide-rapid-regimen',
  type: 'single-select',
  question: 'Select your Semaglutide Rapid Regimen dose',
  subtitle: 'Rapid Regimen - Intended for patients previously or currently on treatment who have NEVER experienced side effects',
  options: [
    { label: 'Semaglutide 0.2mg/0.4mg rapid - Both doses for 2 weeks each', value: 'Semaglutide 0.2mg/0.4mg rapid - Both doses for 2 weeks each' },
    { label: 'Semaglutide 0.6mg/0.9mg rapid - Both doses for 2 weeks each', value: 'Semaglutide 0.6mg/0.9mg rapid - Both doses for 2 weeks each' },
    { label: 'Semaglutide 1.12mg/1.5mg rapid - Both doses for 2 weeks each', value: 'Semaglutide 1.12mg/1.5mg rapid - Both doses for 2 weeks each' },
    { label: 'Semaglutide 1.9mg/2.16mg rapid - Both doses for 2 weeks each', value: 'Semaglutide 1.9mg/2.16mg rapid - Both doses for 2 weeks each' },
    { label: 'Semaglutide 2.3mg rapid - 4 week duration', value: 'Semaglutide 2.3mg rapid - 4 week duration' }
  ]
};

const semaglutideTwiceWeeklyRegimenQuestion = {
  id: 'semaglutide-twice-weekly-regimen',
  type: 'single-select',
  question: 'Select your Semaglutide Twice Weekly Regimen dose',
  subtitle: 'Twice Weekly Regimen - Intended for individuals who report side effects',
  options: [
    { label: 'Semaglutide 0.2mg/0.4mg biweekly - Both doses for 2 weeks each', value: 'Semaglutide 0.2mg/0.4mg biweekly - Both doses for 2 weeks each' },
    { label: 'Semaglutide 0.6mg/0.9mg biweekly - Both doses for 2 weeks each', value: 'Semaglutide 0.6mg/0.9mg biweekly - Both doses for 2 weeks each' },
    { label: 'Semaglutide 1.12mg/1.5mg biweekly - Both doses for 2 weeks each', value: 'Semaglutide 1.12mg/1.5mg biweekly - Both doses for 2 weeks each' },
    { label: 'Semaglutide 1.9mg biweekly - 4 week duration', value: 'Semaglutide 1.9mg biweekly - 4 week duration' },
    { label: 'Semaglutide 2.16mg biweekly - 4 week duration', value: 'Semaglutide 2.16mg biweekly - 4 week duration' },
    { label: 'Semaglutide 2.3mg biweekly - 4 week duration', value: 'Semaglutide 2.3mg biweekly - 4 week duration' }
  ]
};

const tirzepatideAlternativeRegimenQuestion = {
  id: 'tirzepatide-alternative-regimen',
  type: 'single-select',
  question: 'Select your Tirzepatide Alternative Regimen dose',
  subtitle: 'Alternative Regimen - Primary protocol offered by Beluga (mandatory)',
  options: [
    { label: 'Tirzepatide 1.5mg/3mg alternative - Both doses for 2 weeks each', value: 'Tirzepatide 1.5mg/3mg alternative - Both doses for 2 weeks each' },
    { label: 'Tirzepatide 6mg alternative - 4 week duration', value: 'Tirzepatide 6mg alternative - 4 week duration' },
    { label: 'Tirzepatide 9mg alternative - 4 week duration', value: 'Tirzepatide 9mg alternative - 4 week duration' },
    { label: 'Tirzepatide 11mg alternative - 4 week duration', value: 'Tirzepatide 11mg alternative - 4 week duration' },
    { label: 'Tirzepatide 13.5mg alternative - 4 week duration', value: 'Tirzepatide 13.5mg alternative - 4 week duration' }
  ]
};

const tirzepatideRapidRegimenQuestion = {
  id: 'tirzepatide-rapid-regimen',
  type: 'single-select',
  question: 'Select your Tirzepatide Rapid Regimen dose',
  subtitle: 'Rapid Regimen - Secondary protocol (optional). Intended for patients previously or currently on treatment who have NEVER experienced side effects',
  options: [
    { label: 'Tirzepatide 1.5mg/3mg rapid - Both doses for 2 weeks each', value: 'Tirzepatide 1.5mg/3mg rapid - Both doses for 2 weeks each' },
    { label: 'Tirzepatide 6mg/9mg rapid - Both doses for 2 weeks each', value: 'Tirzepatide 6mg/9mg rapid - Both doses for 2 weeks each' },
    { label: 'Tirzepatide 11mg/13.5mg rapid - Both doses for 2 weeks each', value: 'Tirzepatide 11mg/13.5mg rapid - Both doses for 2 weeks each' },
    { label: 'Tirzepatide 13.5mg rapid - 4 week duration', value: 'Tirzepatide 13.5mg rapid - 4 week duration' }
  ]
};

const tirzepatideTwiceWeeklyRegimenQuestion = {
  id: 'tirzepatide-twice-weekly-regimen',
  type: 'single-select',
  question: 'Select your Tirzepatide Twice Weekly Regimen dose',
  subtitle: 'Twice Weekly Regimen - Intended for individuals who report side effects',
  options: [
    { label: 'Tirzepatide 1.5mg/3mg biweekly - Both doses for 2 weeks each', value: 'Tirzepatide 1.5mg/3mg biweekly - Both doses for 2 weeks each' },
    { label: 'Tirzepatide 6mg biweekly - 4 week duration', value: 'Tirzepatide 6mg biweekly - 4 week duration' },
    { label: 'Tirzepatide 9mg biweekly - 4 week duration', value: 'Tirzepatide 9mg biweekly - 4 week duration' },
    { label: 'Tirzepatide 11mg biweekly - 4 week duration', value: 'Tirzepatide 11mg biweekly - 4 week duration' },
    { label: 'Tirzepatide 13.5mg biweekly - 4 week duration', value: 'Tirzepatide 13.5mg biweekly - 4 week duration' }
  ]
};

const dosagePreferenceQuestion = {
  id: 'dosage-preference',
  type: 'single-select',
  question: 'Regarding your dosage, how would you like to continue your treatment?',
  options: [
    { label: 'Stay at the same dose or the closest equivalent dose', value: 'Stay at the same dose or the closest equivalent dose' },
    { label: 'Increase the dose if a higher one is available, or continue with my current dose if it\'s already at the maximum', value: 'Increase the dose if a higher one is available, or continue with my current dose if it\'s already at the maximum' },
    { label: 'Decrease my dose', value: 'Decrease my dose' }
  ]
};

const prescriptionPhotoQuestion = {
  id: 'prescription-photo',
  type: 'single-select-conditional',
  question: 'Do you have a picture of your current prescription? We need this photograph in order to validate your current dosage.',
  options: [
    { label: 'Yes', value: 'yes', requiresUpload: true },
    { label: 'No', value: 'no', starterDose: true }
  ]
};

const uploadPrescriptionQuestion = {
  id: 'upload-prescription',
  type: 'file-upload',
  question: 'Please upload a picture of the prescription or bottle of your current GLP-1/GIP medication',
  subtitle: 'Make sure the entire label is visible'
};

// ============================================
// NEW PATIENT QUESTIONS
// ============================================
const injectionCapabilityQuestion = {
  id: 'injection-capability',
  type: 'single-select',
  question: 'Are you able to safely give yourself injections or have someone help you?',
  options: [
    // 'no' is NOT a disqualifier: HealthIntakeForm hands these patients off to
    // the branded intake (/branded-weight-loss), which carries oral options.
    { label: "No, I'd need an oral option instead", value: 'no' },
    { label: "Yes, I can inject myself or have reliable help", value: 'yes' }
  ]
};

// ============================================
// FINAL QUESTIONS
// ============================================
const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  subtitle: 'Feel free to share any concerns or questions',
  placeholder: 'Type your questions or concerns here...'
};

const treatmentConsentQuestion = {
  id: 'treatment-consent',
  type: 'consent-dropdown',
  question: 'Consent (Individualized Medication Treatment)',
  consentText: `We are committed to providing individualized care tailored to your unique health needs. As part of our treatment options, we are pleased to consider both FDA-approved, brand name medications and compounded alternatives. Our current evaluation considers:

FDA-Approved Brand Name Medications:
• Zepbound
• Mounjaro
• Ozempic
• Wegovy

Compounded Medications:
For patients who may benefit from a more specialized approach, we offer compounded formulations of semaglutide and tirzepatide. These compounded medications can be customized with alternative dosing schedules, alternative dosing frequencies, different routes of administration, and/or additives intended to help reduce side effects commonly seen with the brand name products.

Explanation of Compounded Medications:
Compounded medications are custom-prepared by licensed pharmacists to better meet an individual patient's specific needs. Unlike FDA-approved brand name products, compounded medications are not subject to the same rigorous testing and extensive clinical trials. This means that while they offer the advantage of customization, there may be differences in the consistency, efficacy, and safety profiles compared to those of the branded products.

Consent and Acknowledgment:
By proceeding with this treatment plan, you acknowledge that:
• You have been informed of the available treatment options, including both FDA-approved brand name medications and compounded alternatives.
• You understand that compounded medications are tailored to meet individual needs but are not subjected to the same level of testing as brand name products.
• Your treatment plan will be determined based on your candidacy and specific health requirements, ensuring the most appropriate therapy for you.

Please review this information carefully. If you have any questions or need further clarification regarding your treatment options, do not hesitate to ask our healthcare team. Your health and well-being are our highest priorities, and we are here to support you in making the best-informed decision about your care.`,
  options: [
    { label: 'I have read and understand the above information and I do consent and wish to move forward with this treatment plan', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const finalVerificationQuestion = {
  id: 'final-verification',
  type: 'consent-dropdown',
  question: 'Patient Verification',
  consentText: `I have reviewed, understand, and attest that the information I have provided above is true to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not in the information that I provided above.

For further medical questions, please reach out to your prescriber. If you're having questions regarding shipping or billing, please reach out to the company.`,
  checkbox: {
    text: 'I accept the Beluga Health Privacy Policy, give my Consent, and agree to the Terms of Service.',
    links: [
      { text: 'Privacy Policy', url: 'https://customerconsents.s3.amazonaws.com/Beluga_Health_PA_Privacy_Policy.pdf' },
      { text: 'Consent', url: 'https://customerconsents.s3.amazonaws.com/Beluga_Health_Telemedicine_Informed_Consent.pdf' },
      { text: 'Terms of Service', url: 'https://fitpal.com/terms-and-conditions/' }
    ]
  },
  options: [
    { label: 'I consent and wish to move forward', value: 'consent' },
    { label: 'I do not wish to continue', value: 'decline', disqualifies: true }
  ]
};

// Medication selection question before checkout
const medicationSelectionQuestion = {
  id: 'medication-selection',
  type: 'medication-selection',
  question: 'What core ingredient would you like your provider to consider?',
  options: [
    { label: 'Tirzepatide', value: 'tirzepatide', description: 'Mounjaro, Zepbound' },
    { label: 'Semaglutide', value: 'semaglutide', description: 'Ozempic, Wegovy' }
  ]
};

// Shipping Address Step
const shippingAddressStep = {
  id: 'shipping-address',
  type: 'shipping-address',
  question: 'Shipping Address'
};

// Checkout and Contact
const checkoutStep = {
  id: 'checkout',
  type: 'checkout',
  question: 'Complete Your Order'
};

// ============================================
// POST-CHECKOUT VERIFICATION STEPS
// ============================================
const identityVerificationStep = {
  id: 'identity-verification',
  type: 'file-upload',
  question: 'Identity Verification',
  subtitle: `Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible.

If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card.

Make sure the photo is clear and legible.`
};

const fullBodyImageStep = {
  id: 'full-body-image',
  type: 'file-upload',
  question: 'Full Body Image',
  subtitle: `To ensure the highest quality of care during your telemedicine consultation, we kindly request your assistance in providing a full-body clothed photo of yourself (please remain clothed but avoid wearing baggy apparel).

The image should include your face to help facilitate verification of your identity. Please be assured that these images will remain private and will not be shared. The physician will use the picture solely for assessment purposes to better understand your health condition.

Thank you for your cooperation and understanding in helping to provide comprehensive care remotely.

(Tip: using a full length mirror can help you capture this image)`
};

const truthfulnessConsentStep = {
  id: 'truthfulness-consent',
  type: 'consent-dropdown',
  question: 'Consent (Truthfulness)',
  consentText: `Please attest to the following confirming that all information you have provided to us is true and complete.

I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge.

I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.`,
  options: [
    { label: 'I have read the above information and I do consent and wish to move forward', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const glp1ConsentStep = {
  id: 'glp1-consent',
  type: 'consent-dropdown',
  question: 'Consent (GLP-1 and GLP-1/GIP)',
  consentText: `Indication for Use:
You are requesting treatment with a GLP-1 (Ozempic, Wegovy, or compounded semaglutide) or GIP/GLP-1 receptor agonist (Mounjaro, Zepbound, or compounded tirzepatide) medication as part of your treatment plan for the management of weight or obesity. These medications work by mimicking the action of incretin hormones, which help regulate blood sugar levels, promote feeling full, and reduce food intake.

Potential Benefits:
• Weight loss or weight management
• Improved blood glucose control
• Reduced cardiovascular risk
• Potential improvement in overall metabolic health

Potential Side Effects:
While these medications can be beneficial, they may also cause side effects. Although not common, these medications can result in emergency room visits, hospitalizations, or even death.

Common Side Effects:
• Nausea
• Vomiting
• Diarrhea
• Constipation
• Decreased appetite
• Indigestion

Serious Side Effects:
• Pancreatitis (inflammation of the pancreas)
• Hypoglycemia (low blood sugar), especially when used with other diabetes medications
• Gallbladder disease (e.g., gallstones)
• Kidney problems
• Allergic reactions (e.g., rash, itching, swelling)
• Gastroparesis (paralysis of the bowels)

Risks and Considerations:
Pancreatitis: There is a risk of developing pancreatitis. If you experience severe abdominal pain, nausea, or vomiting, you should contact your healthcare provider immediately.

Thyroid Tumors: Animal studies have shown an increased risk of thyroid tumors with certain GLP-1 medications. Although this has not been confirmed in humans, please inform your healthcare provider if you have a history of thyroid cancer.

Hypoglycemia: When taken with other diabetes medications, particularly insulin or sulfonylureas, there is a risk of low blood sugar. It is important that your provider knows if any of these medications are added to your regimen.

Kidney Function: This medication may affect kidney function, particularly in patients with existing kidney disease. Regular monitoring of kidney function may be required.

Monitoring and Follow-up:
You will require regular follow-up visits to monitor your response to the medication and to assess for any side effects. We may intermittently ask for full-body selfie images to ensure that your reported weight is consistent.

I acknowledge the potential benefits, risks, and side effects of GLP-1 or GIP/GLP-1 receptor agonist medications. I understand the importance of regular monitoring and follow-up appointments. I consent to the use of GLP-1 or GIP/GLP-1 receptor agonist medications as part of my treatment plan for overweight or obesity.`,
  options: [
    { label: 'I have read and understand the above information and I wish to continue', value: 'consent' },
    { label: 'No — I do not wish to continue with treatment', value: 'decline', disqualifies: true }
  ]
};

const completeStep = {
  id: 'complete',
  type: 'complete',
  question: 'Complete'
};

// ============================================
// PATIENT ON TREATMENT FLOW (Complete flow)
// ============================================
export const patientOnTreatmentFlow = [
  lastMedicationDateQuestion,
  // doseDecreaseQuestion - conditionally inserted based on lastMedicationDateQuestion
  // restartMedicationQuestion - conditionally inserted based on lastMedicationDateQuestion
  lastMedicationDateInputQuestion,
  currentDoseQuestion,
  brandOrCompoundedQuestion,
  // medicationDetailsQuestion - conditionally inserted if compounded selected
  sideEffectsQuestion,
  medicationProgressQuestion,
  // rapidTitrationQuestion - conditionally inserted based on medicationProgressQuestion
  // twiceWeeklyQuestion - conditionally inserted based on medicationProgressQuestion
  // Titration regimen questions - conditionally inserted based on medication type and selected regimen
  dosagePreferenceQuestion,
  prescriptionPhotoQuestion
  // uploadPrescriptionQuestion - conditionally inserted if prescription-photo is 'yes'
];

// ============================================
// NEW PATIENT FLOW (Complete flow)
// ============================================
export const newPatientFlow = [
  injectionCapabilityQuestion
];

// ============================================
// BASE QUESTIONS (after weight loss goal question)
// ============================================

export const  removeUrlParameter=(parameterName)=> {
    const url = new URL(window.location.href);
    url.searchParams.delete(parameterName);
    
    // Update the URL in the address bar without a page refresh
    window.history.replaceState({}, document.title, url.toString());
}

const  checkIfPaymentAlreadyMade= async()=>{

  const queryString = window.location.search;

// 2. Create a URLSearchParams object
const urlParams = new URLSearchParams(queryString);

const payment_id=urlParams.get('payment_id'); 
console.log('payment_id0',payment_id)

if(payment_id!==null && payment_id!==undefined && payment_id!=='')
{

  const response=await apiService.getFormDetailsWithPaymentId(payment_id)

 

if(response.formdata!==null && response.formdata.completed!==true )
{

  const resFormData=JSON.parse(response.formdata.quiz_data)

      const progressData = {
          primaryGoal:resFormData.primaryGoal,
          currentStep:resFormData.currentStep,
          answers:resFormData.answers,
          timestamp: resFormData.timestamp || Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(progressData));
          localStorage.setItem('fitpal_intake_payment_id', payment_id);
 return true
}
else{
       localStorage.removeItem(STORAGE_KEY);
       localStorage.removeItem('fitpal_intake_payment_id');
       localStorage.removeItem('fitpal_intake_form_is_back_clicked')
return false
}
  
}
else{
     localStorage.removeItem('fitpal_intake_payment_id');
     localStorage.removeItem('fitpal_intake_form_is_back_clicked')
  return false
}




}

let payment=await checkIfPaymentAlreadyMade();



 const baseQuestions = [
  weightLossImprovementQuestion,
  medicalConditionsQuestion,
  // gallbladderConsentQuestion - dynamically inserted if needed
  // medicalConditionDetailsQuestion - dynamically inserted if needed
  currentMedicalConditions,
  medicationsListQuestion,
  allergiesQuestion,
  sexAtBirthQuestion,
  basicContactQuestion,
  pregnancyQuestion,
  pregnancyConsentQuestion,
  highestWeightQuestion,
  heightWeightQuestion,
  diagnosisTimeQuestion,
  weightManagementApproachQuestion,
  activityLevelQuestion,
  dietaryPlanQuestion,
  gastricBypassQuestion,
  glpAllergiesQuestion,
  diabetesMedicationsQuestion,
  currentGlpMedicationsQuestion
  // After this: either patientOnTreatmentFlow OR newPatientFlow
];




// ============================================
// FINAL QUESTIONS (after conditional flow)
// ============================================
let finalQuestions=[];

if(!payment)
{

 finalQuestions = [
  additionalQuestionsQuestion,
  treatmentConsentQuestion,
  finalVerificationQuestion,
  medicationSelectionQuestion,
  truthfulnessConsentStep,
  glp1ConsentStep,
  // Beluga requires a full-body photo + government photo ID (both required).
  // Collected BEFORE checkout (consistent with every other visit type) and
  // relayed to Beluga post-submit via uploadVisitPhoto (HealthIntakeForm).
  fullBodyImageStep,
  identityVerificationStep,
  shippingAddressStep,
  checkoutStep,
  completeStep,
];

}
else{

   finalQuestions = [
  additionalQuestionsQuestion,
  treatmentConsentQuestion,
  finalVerificationQuestion,
  // medicationSelectionQuestion,
  truthfulnessConsentStep,
  glp1ConsentStep,
  fullBodyImageStep,
  identityVerificationStep,
  shippingAddressStep,
];

}

// ============================================
// LOSE WEIGHT FLOW (base - dynamic questions will be inserted)
// ============================================
export const loseWeightFlow = [
  ...baseQuestions,
  // Placeholder for conditional flow (will be handled dynamically)
  ...newPatientFlow, // Default to new patient flow, will be replaced if on treatment
  ...finalQuestions
];

// ============================================
// MAINTAIN WEIGHT FLOW
// ============================================
export const maintainWeightFlow = [
  medicalConditionsQuestion,
  currentMedicalConditions,
  medicationsListQuestion,
  allergiesQuestion,
  sexAtBirthQuestion,
  basicContactQuestion,
  pregnancyQuestion,
  pregnancyConsentQuestion,
  highestWeightQuestion,
  heightWeightQuestion,
  diagnosisTimeQuestion,
  weightManagementApproachQuestion,
  activityLevelQuestion,
  dietaryPlanQuestion,
  gastricBypassQuestion,
  glpAllergiesQuestion,
  diabetesMedicationsQuestion,
  currentGlpMedicationsQuestion,
  // Patient on treatment flow is always shown for maintain weight
  ...patientOnTreatmentFlow,
  ...finalQuestions
];

// ============================================
// MAIN QUESTION (Entry Point) - Start with weight loss goal
// ============================================
export const mainQuestion = {
  id: 'weight-loss-goal',
  type: 'single-select',
  question: 'What is your weight loss goal?',
  options: [
    { label: '10 to 20 lbs', value: '10 to 20 lbs' },
    { label: '21 to 50 lbs', value: '21 to 50 lbs' },
    { label: '51 to 100 lbs', value: '51 to 100 lbs' },
    { label: 'Over 100 lbs', value: 'over 100 lbs' }
  ]
};

// Flow mapping - now directly goes to lose weight flow
export const flowMapping = {
  '10-20': loseWeightFlow,
  '21-50': loseWeightFlow,
  '51-100': loseWeightFlow,
  'over-100': loseWeightFlow
};

// Get flow by goal
export const getFlowByGoal = (goal) => {
  return flowMapping[goal] || loseWeightFlow;
};

// Export individual questions for dynamic insertion
export const conditionalQuestions = {
  gallstoneConsentQuestion,
  cholecystectomyConsentQuestion,
  thyroidConsentQuestion,
  bmiConsentQuestion,
  medicalConditionDetailsQuestion,
  lastMedicationDateQuestion,
  lastMedicationDateInputQuestion,
  doseDecreaseQuestion,
  restartMedicationQuestion,
  currentDoseQuestion,
  brandOrCompoundedQuestion,
  medicationDetailsQuestion,
  sideEffectsQuestion,
  fatigueAdditiveQuestion,
  nauseaAdditiveQuestion,
  muscleAdditiveQuestion,
  medicationProgressQuestion,
  rapidTitrationQuestion,
  twiceWeeklyQuestion,
  alternativeRegimenInfoQuestion,
  dosagePreferenceQuestion,
  prescriptionPhotoQuestion,
  uploadPrescriptionQuestion,
  injectionCapabilityQuestion,
  alternativeRegimenInfoQuestion,
  // Legacy titration regimen questions
  semaglutideAlternativeRegimenQuestion,
  semaglutideRapidRegimenQuestion,
  semaglutideTwiceWeeklyRegimenQuestion,
  tirzepatideAlternativeRegimenQuestion,
  tirzepatideRapidRegimenQuestion,
  tirzepatideTwiceWeeklyRegimenQuestion
};

// Export arrays for flow building
export const patientOnTreatmentQuestions = patientOnTreatmentFlow;
export const newPatientQuestions = newPatientFlow;
